import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { LoadingType } from "../../../types/CommonTypes";
import {
  getSalesConfigList,
  saveSalesConfig as saveSalesConfigApi,
  updateSalesConfig as updateSalesConfigApi,
  SalesConfigPayload,
} from "../../../service/SalesConfigService";
import { useAppSelector } from "../redux-hooks";
import { SalesConfigRecord } from "../../../components/operation/isr/isrTypes";

type SalesConfigState = {
  salesConfigList: SalesConfigRecord[];
  fetchSalesConfigListStatus: LoadingType;
  saveSalesConfigStatus: LoadingType;
  updateSalesConfigStatus: LoadingType;
};

const initialState: SalesConfigState = {
  salesConfigList: [],
  fetchSalesConfigListStatus: "idle",
  saveSalesConfigStatus: "idle",
  updateSalesConfigStatus: "idle",
};

const mapTargetRate = (row: any) => ({
  targetRange: row?.targetRange != null
    ? String(row.targetRange)
    : (row?.displayLabel != null ? String(row.displayLabel) : ""),
  targetCompPercent: row?.targetCompPercent != null ? String(row.targetCompPercent) : "",
  active: row?.active !== false,
});

const mapSalesConfigRecord = (row: any): SalesConfigRecord => ({
  salesConfigId: row?.salesConfigId,
  isrId: row?.isrId,
  invoiceYear: row?.invoiceYear != null ? String(row.invoiceYear) : "",
  effectiveFrom: row?.effectiveFrom ?? "",
  effectiveTo: row?.effectiveTo ?? "",
  alignment: row?.alignment ?? "",
  scope: row?.scope ?? "",
  rateType: row?.rateType === "TARGET_BASED" ? "TARGET_BASED" : "STANDARD",
  standardCompPercent: row?.standardCompPercent != null ? String(row.standardCompPercent) : "",
  targetRates: Array.isArray(row?.targetRates) ? row.targetRates.map(mapTargetRate) : [],
  isrName: row?.isrName,
});

export const fetchSalesConfigList = createAsyncThunk("fetchSalesConfigList", async () => {
  const response = await getSalesConfigList();
  return response.data;
});

const toRejectedPayload = (error: any, fallback = "Unable to save Sales Config.") =>
  error?.data ?? error?.response?.data ?? { message: fallback };

export const saveSalesConfig = createAsyncThunk(
  "saveSalesConfig",
  async (payload: SalesConfigPayload, { rejectWithValue }) => {
    try {
      const response = await saveSalesConfigApi(payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(toRejectedPayload(error));
    }
  }
);

export const updateSalesConfig = createAsyncThunk(
  "updateSalesConfig",
  async (payload: SalesConfigPayload, { rejectWithValue }) => {
    try {
      const response = await updateSalesConfigApi(payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(toRejectedPayload(error));
    }
  }
);

const salesConfigSlice = createSlice({
  name: "salesConfig",
  initialState,
  reducers: {
    resetSalesConfigSlice: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSalesConfigList.pending, (state) => {
        state.fetchSalesConfigListStatus = "loading";
      })
      .addCase(fetchSalesConfigList.fulfilled, (state, action) => {
        state.fetchSalesConfigListStatus = "success";
        const rows = action.payload?.data;
        state.salesConfigList = Array.isArray(rows) ? rows.map(mapSalesConfigRecord) : [];
      })
      .addCase(fetchSalesConfigList.rejected, (state) => {
        state.fetchSalesConfigListStatus = "error";
      })
      .addCase(saveSalesConfig.pending, (state) => {
        state.saveSalesConfigStatus = "loading";
      })
      .addCase(saveSalesConfig.fulfilled, (state) => {
        state.saveSalesConfigStatus = "success";
      })
      .addCase(saveSalesConfig.rejected, (state) => {
        state.saveSalesConfigStatus = "error";
      })
      .addCase(updateSalesConfig.pending, (state) => {
        state.updateSalesConfigStatus = "loading";
      })
      .addCase(updateSalesConfig.fulfilled, (state) => {
        state.updateSalesConfigStatus = "success";
      })
      .addCase(updateSalesConfig.rejected, (state) => {
        state.updateSalesConfigStatus = "error";
      });
  },
});

export const { resetSalesConfigSlice } = salesConfigSlice.actions;
export const useSalesConfigReducer = () => useAppSelector((state) => state.salesConfigSlice);
export default salesConfigSlice.reducer;
